import { useId } from 'react';

import { FIGURE } from '../diagramTheme';

// ```numberline - a number line with points and intervals, as JSON:
//
//   { "min": -5, "max": 5, "step": 1,
//     "ranges": [{ "from": -1, "to": 3, "openFrom": true }],
//     "points": [{ "at": 2, "label": "x = 2" }] }
//
// `openFrom` / `openTo` draw an open circle (strict inequality); a range with
// no `to` (or no `from`) runs off the end with an arrow. Plain SVG in the
// figure theme.

export interface NumberLineSpec {
  min: number;
  max: number;
  step?: number;
  ranges?: Array<{
    from?: number;
    to?: number;
    openFrom?: boolean;
    openTo?: boolean;
  }>;
  points?: Array<{ at: number; label?: string; open?: boolean }>;
}

const W = 480;
const LEFT = 30;
const RIGHT = 450;
const AXIS_Y = 58;

function End({ x, open }: { x: number; open?: boolean }) {
  return (
    <circle
      cx={x}
      cy={AXIS_Y}
      r={6}
      fill={open ? FIGURE.paper : FIGURE.series[0]}
      stroke={FIGURE.series[0]}
      strokeWidth={2.5}
    />
  );
}

export function numberLineLabel(spec: NumberLineSpec): string {
  const parts = (spec.ranges ?? []).map(r => {
    const from =
      r.from === undefined ? '' : `${r.from} ${r.openFrom ? '<' : '≤'} `;
    const to = r.to === undefined ? '' : ` ${r.openTo ? '<' : '≤'} ${r.to}`;
    return `${from}x${to}`;
  });
  return `Number line from ${spec.min} to ${spec.max}${parts.length ? `, showing ${parts.join(' and ')}` : ''}`;
}

export function NumberLineFigure({ spec }: { spec: NumberLineSpec }) {
  // Its own marker ids. A fixed `nl-arrow` was shared by every number line on
  // the page, and url(#nl-arrow) resolves to the first in the document: each
  // figure's arrows were drawn from another's markers, and went with it.
  const uid = useId().replace(/:/g, '');
  const arrow = `nl-arrow-${uid}`;
  const rangeArrow = `nl-arrow-range-${uid}`;
  const step = spec.step ?? 1;
  const span = spec.max - spec.min || 1;
  const x = (v: number) => LEFT + ((v - spec.min) / span) * (RIGHT - LEFT);
  const ticks: number[] = [];
  for (let v = spec.min; v <= spec.max + 1e-9; v += step) {
    ticks.push(Math.round(v * 1000) / 1000);
  }
  return (
    <svg viewBox={`0 0 ${W} 96`} width={W} height={96}>
      <line
        x1={LEFT - 18}
        x2={RIGHT + 18}
        y1={AXIS_Y}
        y2={AXIS_Y}
        stroke={FIGURE.muted}
        strokeWidth={1.5}
        markerStart={`url(#${arrow})`}
        markerEnd={`url(#${arrow})`}
      />
      <defs>
        <marker
          id={arrow}
          viewBox='0 0 10 10'
          refX='5'
          refY='5'
          markerWidth='7'
          markerHeight='7'
          orient='auto-start-reverse'
        >
          <path d='M0,0 L10,5 L0,10 z' fill={FIGURE.muted} />
        </marker>
        <marker
          id={rangeArrow}
          viewBox='0 0 10 10'
          refX='5'
          refY='5'
          // In pixels, not stroke widths: a marker scales with the line that
          // uses it, and on the 5px range line six stroke widths drew a 30px
          // head - three times the axis's.
          markerUnits='userSpaceOnUse'
          markerWidth='13'
          markerHeight='13'
          orient='auto-start-reverse'
        >
          <path d='M0,0 L10,5 L0,10 z' fill={FIGURE.series[0]} />
        </marker>
      </defs>
      {ticks.map(t => (
        <g key={t}>
          <line
            x1={x(t)}
            x2={x(t)}
            y1={AXIS_Y - 5}
            y2={AXIS_Y + 5}
            stroke={FIGURE.muted}
            strokeWidth={1.2}
          />
          <text
            x={x(t)}
            y={AXIS_Y + 22}
            textAnchor='middle'
            fontFamily={FIGURE.mono}
            fontSize={11}
            fill={FIGURE.muted}
          >
            {t}
          </text>
        </g>
      ))}
      {(spec.ranges ?? []).map((r, i) => {
        const from = r.from === undefined ? LEFT - 14 : x(r.from);
        const to = r.to === undefined ? RIGHT + 14 : x(r.to);
        return (
          <g key={`r${i}`}>
            <line
              x1={from}
              x2={to}
              y1={AXIS_Y}
              y2={AXIS_Y}
              stroke={FIGURE.series[0]}
              strokeWidth={5}
              strokeLinecap='round'
              markerStart={
                r.from === undefined ? `url(#${rangeArrow})` : undefined
              }
              markerEnd={r.to === undefined ? `url(#${rangeArrow})` : undefined}
            />
            {r.from !== undefined && <End x={x(r.from)} open={r.openFrom} />}
            {r.to !== undefined && <End x={x(r.to)} open={r.openTo} />}
          </g>
        );
      })}
      {(spec.points ?? []).map((p, i) => (
        <g key={`p${i}`}>
          <End x={x(p.at)} open={p.open} />
          {p.label && (
            <text
              x={x(p.at)}
              y={AXIS_Y - 16}
              textAnchor='middle'
              fontFamily={FIGURE.font}
              fontSize={12}
              fontWeight={600}
              fill={FIGURE.brandDeep}
            >
              {p.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}
